import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { ObjectUtils } from 'primeng/utils';
import { BehaviorSubject, forkJoin, Observable, Subject, take, takeUntil } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { AuthRoles } from '../auth/auth.types';
import { FilterService } from '../services/filter.service';
import { LocationService } from '../services/location.service';
import { LookupService } from '../services/lookup.service';
import { PreviousRouteService } from '../services/previous-route.service';
import { AppConstants } from '../shared/app.constants';
import { AppPermissions } from '../shared/app.permissions';
import { DateRangeFilterComponent } from '../shared/components/date-range-filter/date-range-filter.component';
import { GeographicFilterComponent } from '../shared/components/geographic-filter/geographic-filter.component';
import { DateRangeFilterModel } from '../shared/models/date-range-filter-model';
import { GeographicFilterModel } from '../shared/models/geographic-filter-model';
import { TrimTextTransformPipe } from '../shared/pipes/trim-text-transform.pipe';

@Component({
  selector: 'ness-locations',
  templateUrl: './locations.component.html',
  providers: [TrimTextTransformPipe]
})
export class LocationsComponent implements OnInit, OnDestroy, AfterViewInit {
  private _address: BehaviorSubject<ServerModels.ENESS_AddressDTO> = new BehaviorSubject(null);
  address$: Observable<ServerModels.ENESS_AddressDTO> = this._address.asObservable();
  addresses: ServerModels.AddressViewModel[] = [];
  canCreate: AuthRoles[] = ['Editor', 'Application Administrator', 'Power User', 'NESS Administrator'];
  cols: any[];
  dateFilterModel: DateRangeFilterModel = null;
  geographicFilter: GeographicFilterModel = null;
  hasDeletePermission: boolean = false;
  hasEditPermission = this.authService.hasUserTypeRole(AppPermissions.canEditLocation.roleUserType);
  loading: boolean;
  loadingEndRow: number = AppConstants.dbPagingDefaultRows;
  loadingStartRow: number = 1;
  loadingStartRow$: BehaviorSubject<number> = new BehaviorSubject<number>(this.loadingStartRow);
  loadingEndRow$: BehaviorSubject<number> = new BehaviorSubject<number>(this.loadingEndRow);
  messageTemplate: string;
  openLocationViewDetailForm: boolean = false;
  rows: number = AppConstants.defaultTableRows;
  selectedAddress: ServerModels.ENESS_Address;
  canView = AppPermissions.canViewLocationFilters;
  readonly locationsStorageKey: string = 'locationsFilterModel';
  readonly geoFilterStorageKey: string = 'geoFilterStorageKeyLocations';
  @ViewChild(DateRangeFilterComponent, { static: false }) dateRangeFilterCmp: DateRangeFilterComponent;
  @ViewChild(GeographicFilterComponent, { static: false }) geoFilterCmp: GeographicFilterComponent;
  @ViewChild(Table, { static: false }) dataTable: Table;
  private _unsubscribe$: Subject<void> = new Subject<void>();
  showLocationDetailsView: boolean = true;
  showEditRMSLocationView: boolean = false;
  searchCols: any[];
  isStateLocalLE: boolean = false;
  showAllLocations: boolean = false;
  showFilterResults: boolean = false;
  showSearchResults: boolean = false;
  _addressResults: ServerModels.ENESS_AddressDTO[] = [];
  addressResults: BehaviorSubject<ServerModels.ENESS_AddressDTO[]> = new BehaviorSubject<ServerModels.ENESS_AddressDTO[]>(this._addressResults);
  addressResults$: Observable<ServerModels.ENESS_AddressDTO[]> = this.addressResults.asObservable();
  findLocationFormGroup: FormGroup = this.fb.group({
    FindLocationStreetAddressID: ['', [Validators.required, Validators.maxLength(80)]],
    FindLocationUnitID: ['', [Validators.maxLength(80)]],
    FindLocationCityNameID: '',
    FindLocationStateID: '',
    FindLocationPostalCodeID: ['', [Validators.pattern('[0-9]{5}')]],
    FindLocationPostalCodeExtendedID: ['', [Validators.pattern('[0-9]{4}')]]
  });

  constructor(private locationService: LocationService,
    private messageService: MessageService,
    private filterService: FilterService,
    private authService: AuthService,
    public hostElement: ElementRef,
    public lookupService: LookupService,
    private trimTextTransform: TrimTextTransformPipe,
    private fb: FormBuilder,
    private previousRouteService: PreviousRouteService,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    if (!this.authService.hasRole(['NESS Administrator']) && this.authService.hasUserType(['StateLocalLE'])) {
      this.filterService.geographicFilterModel$.next(null);
      this.isStateLocalLE = true;
    }

    this.cols = [
      { field: 'StreetAddress', header: 'Address' },
      { field: 'City', header: 'City' },
      { field: 'State', header: 'State' },
      { field: 'PostalCode', header: 'Postal code' },
      { field: 'EventCount', header: 'Related Events Based on Filters' }
    ];

    this.searchCols = [
      { field: 'StreetAddressWithUnit', header: 'Address' },
      { field: 'CityName', header: 'City' },
      { field: 'StateName', header: 'State' },
      { field: 'PostalCode', header: 'Postal code' }
    ];
  }

  ngOnDestroy(): void {
    this.messageService.clear('locationsMsgs');
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  ngAfterViewInit(): void {
    this._setFilters();
    this.changeDetectorRef.detectChanges();
  }

  public onMaskBlur(field: string): void {
    let ctrl = this.findLocationFormGroup.get(field);
    if (ctrl.invalid) {
      ctrl.reset();
    }
  }

  public toggleFilterGrid(b: boolean): void {
    this.showSearchResults = !b;
    this.showFilterResults = b;
  }

  public toggleSearchGrid(b: boolean): void {
    this.showSearchResults = b;
    this.showFilterResults = !b;
  }

  // Function called by address filter on focus so that results populate since AfterViewInit triggers when table does not exist
  public fixFilterTable(): void {
    if (this.showFilterResults) {
      this.dataTable.filterService.filters['streetAddress'] = (value:any, filter:any): boolean => {
        if (filter === undefined || filter === null || (typeof filter === 'string' && filter.trim() === '')) {
          return true;
        }

        if (!value  || !value.length) {
          return false;
        }

        let filterValue = ObjectUtils.removeAccents(filter.toString()).toLowerCase().replace(/\s/g, '');
        let stringValue = ObjectUtils.removeAccents(value.toString()).toLowerCase().replace(/\s/g, '');

        return stringValue.indexOf(filterValue) !== -1;
      }
    }
  }

  private _setFilters(): void {
    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFFieldDivision', 'ATFHQ'])) {
      this.geoFilterCmp.applyFilters();
      let storedGeoFilterModel = JSON.parse(sessionStorage.getItem(this.geoFilterStorageKey));
      this.geographicFilter = new GeographicFilterModel(storedGeoFilterModel['_fieldDivisionID'],
        storedGeoFilterModel['_fieldDivisionGroupID'],
        storedGeoFilterModel['_fieldDivisionGroups'],
        storedGeoFilterModel['_stateID'],
        storedGeoFilterModel['_countyID'],
        storedGeoFilterModel['_counties'],
        storedGeoFilterModel['_agencyID'],
        storedGeoFilterModel['_agencies'],
        storedGeoFilterModel['_fieldDivisionName'],
        storedGeoFilterModel['_groupName'],
        storedGeoFilterModel['_groupNameList'],
        storedGeoFilterModel['_stateName'],
        storedGeoFilterModel['_countyName'],
        storedGeoFilterModel['_countyNameList'],
        storedGeoFilterModel['_agencyName'],
        storedGeoFilterModel['_agencyNamesList']
      );
    } else {
      this.filterService.geographicFilterModel$.next(null);
    }

    this.dateRangeFilterCmp.applyFilters();
    let storedDateFilter = JSON.parse(sessionStorage.getItem(this.locationsStorageKey));
    this.dateFilterModel = new DateRangeFilterModel(storedDateFilter['_fieldDivisionID'],
      storedDateFilter['_duration'],
      storedDateFilter['_startDate'],
      storedDateFilter['_endDate']);
  }

  public applyFilters(): void {
    this.toggleFilterGrid(true);
    this._setFilters();
    this.messageTemplate = 'Loading locations...';
    this.addresses = [];
    this.showAllLocations = false;
    if (this.dataTable) {
      this.dataTable.reset();
    }
    this._clearLoadingStatus();
    this.getAllAddresses(0);
  }

  public onSearchLocation() {
    if (this.findLocationFormGroup.valid) {
      let locationSearch = this.findLocationFormGroup.value;
      let streetAddressValue = this.trimTextTransform.transform(locationSearch.FindLocationStreetAddressID);
      let unitValue = this.trimTextTransform.transform(locationSearch.FindLocationUnitID);
      let cityNameValue = this.trimTextTransform.transform(locationSearch.FindLocationCityNameID);
      let stateValue = this.trimTextTransform.transform(locationSearch.FindLocationStateID);
      let postalCodeValue = this.trimTextTransform.transform(locationSearch.FindLocationPostalCodeID);
      let postalCodeExtendedValue = this.trimTextTransform.transform(locationSearch.FindLocationPostalCodeExtendedID);
      this.toggleSearchGrid(true);
      this.findAddresses(streetAddressValue, unitValue, cityNameValue, stateValue, postalCodeValue, postalCodeExtendedValue);
    }
  }

  public resetDefaultFilters(): void {
    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFFieldDivision', 'ATFHQ'])) {
      this.geoFilterCmp.resetToDefaultFilters();
    }
    this.dateRangeFilterCmp.resetToDefaultFilters();
  }

  public clearAppliedFilters(): void {
    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFFieldDivision', 'ATFHQ'])) {
      this.filterService.fieldDivisionName$.next(null);
      this.filterService.groupName$.next(null);
      this.filterService.groupNameList$.next(null);
      this.filterService.stateName$.next(null);
      this.filterService.countyName$.next(null);
      this.filterService.countyNameList$.next(null);
      this.filterService.agencyNames$.next(null);
      this.filterService.agencyNameList$.next(null);
    }

    this.addresses = [];
    this.filterService.dateRangeFilterModel$.next(null);
    this.showAllLocations = true;
    this.dataTable.reset();
    this._clearLoadingStatus();
    this.getAllAddresses(0);
  }

  private getAllAddresses(pageNumber: number = 0) {
    this._setFilters();

    let filtersVM = {
      FieldDivisionID: null,
      FieldDivisionGroupIDs: [],
      AgencyName: null,
      StateID: null,
      CountyIDs: [],
      AgencyIDs: [],
      StartDate: this.showAllLocations ? null : this.dateFilterModel?.startDate,
      EndDate: this.showAllLocations ? null : this.dateFilterModel?.endDate,
      PageNumber: pageNumber,
      RowsPerPage: AppConstants.dbPagingDefaultRows
    } as ServerModels.FiltersViewModel;

    if ((this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFFieldDivision', 'ATFHQ'])) && !this.showAllLocations) {
      filtersVM.FieldDivisionID = this.geographicFilter.fieldDivisionID;
      filtersVM.FieldDivisionGroupIDs = this.geographicFilter.fieldDivisionGroups.map(x => x.IntelligenceReportATFReferralGroupsID);
      filtersVM.StateID = this.geographicFilter.stateID;
      filtersVM.CountyIDs = this.geographicFilter.counties.map(x => x.CountyID);
      filtersVM.AgencyIDs = this.geographicFilter.agencies?.map(x => x.AgencyID);
    } else {
      filtersVM.FieldDivisionID = null;
      filtersVM.FieldDivisionGroupIDs = [];
      filtersVM.StateID = null;
      filtersVM.CountyIDs = [];
      filtersVM.AgencyIDs = [];
    }

    this.loading = true;
    this.locationService.getAllAddresses(filtersVM).pipe(
      takeUntil(this._unsubscribe$)
    ).subscribe({
      next: (addresses: ServerModels.AddressViewModel[]) => {
        this.loading = addresses?.length > 0;
        if (addresses?.length > 0) {
          let nextPage: number = pageNumber + 1;
          this.loadingStartRow = pageNumber == 0 ? 1 : 1 + this.addresses.length;
          this.addresses = this.addresses.concat(addresses);
          this.loadingEndRow = this.addresses.length;
          this.loadingStartRow$.next(this.loadingStartRow);
          this.loadingEndRow$.next(this.loadingEndRow);
          this.messageTemplate = `Loaded rows ${this.loadingStartRow} thru ${this.loadingEndRow}...`;
          if (this.loading = addresses.length == AppConstants.dbPagingDefaultRows) {
            this.getAllAddresses(nextPage);
          }
        }
      },
      error: (err:any) => {
        this.loading = false;
        this.messageService.add({ key: 'locationsMsgs', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message });
      }
    });
  }

  public findAddresses(streetAddress: string, unit: string, cityName: string, state: string, postalCode: string, postalCodeExtended: string): void {
    this._addressResults = [];
    this.addressResults.next(this._addressResults);
    this.locationService.searchAddresses(streetAddress, unit, cityName, state, postalCode, postalCodeExtended).pipe(take(1)).subscribe((address: ServerModels.ENESS_AddressDTO[]) => {
      this._addressResults = [...(address || [])];
      this.addressResults.next(this._addressResults);
    },
      (err:any) => this.messageService.add({ key: 'locationsMsgs', severity: 'error', summary: '', detail: err.error })
    );
  }

  private _clearLoadingStatus(): void {
    this.loadingStartRow = 1;
    this.loadingEndRow = AppConstants.dbPagingDefaultRows;
    this.loadingStartRow$.next(this.loadingStartRow);
    this.loadingEndRow$.next(this.loadingEndRow);
  }

  public viewDetails(addressId: string): void {
    this.openLocationViewDetailForm = true;
    forkJoin({
      address: this.locationService.getAddressDetailsById(addressId),
      canDelete: this.locationService.hasDeletePermission(addressId),
      leCases: this.locationService.getAddressLECases(addressId),
      people: this.locationService.getAddressPeople(addressId)
    }).pipe(take(1)).subscribe({
      next: (val: { address: ServerModels.ENESS_AddressDTO, canDelete: boolean, leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[] }) => {
        this._address.next(val.address);
        this.locationService.addressLECasesSubj.next(val.leCases);
        this.hasDeletePermission = val.canDelete;
        this.locationService.addressPeopleSubj.next(val.people);
      }
    });
  }

  public onShowSidebar(): void {
    window.setTimeout(() => {
      let targetEl = this.hostElement.nativeElement.getElementsByClassName('ui-sidebar-active');
      if (targetEl.length) {
        targetEl[0].scrollTop = 0;
        targetEl[0].setAttribute("tabindex", "-1");
        targetEl[0].focus();
      }
    }, 1000);
  }

  public onCloseSidebar(): void {
    this.onCloseLocation();
    this._address.next(null);
    this.locationService.addressLECasesSubj.next(null);
    this.locationService.addressPeopleSubj.next(null);
    this.openLocationViewDetailForm = false;
  }

  public deleteLocationCompleted(data: string): void {
    if (data != null) {
      this.openLocationViewDetailForm = false;
      let index = this.addresses.indexOf(this.addresses.find(x => x.AddressID == data));
      // updates table in case filter is in place so deleted location does not persist    
      this.addresses = [];
      this.dataTable.reset();
      this._clearLoadingStatus();
      this.getAllAddresses(0);
    }
  }

  public newLocation(): void {
    this.previousRouteService.returnUrl = this.router.url;
    this.previousRouteService.transferData = {
      'isEditForm': false,
      'isEventLocationForm': false,
      'isPersonAddressForm': false
    };
    this.router.navigate(['/location-form']);
  }

  public onCloseLocation(): void {
    if (this.showEditRMSLocationView) {
      this.showEditRMSLocationView = false;
      this.showLocationDetailsView = true;
    }
    else if (this.showLocationDetailsView) {
      this._address.next(null);
      this.locationService.addressLECasesSubj.next(null);
      this.locationService.addressPeopleSubj.next(null);
      this.openLocationViewDetailForm = false;
    }
  }


  public showEditLocation(evt: string): void {
    this.showLocationDetailsView = false;
    this.showEditRMSLocationView = true;
  }

  public onLocationFormSaved(location: ServerModels.ENESS_AddressDTO): void {
    this.showEditRMSLocationView = false;
    this.showLocationDetailsView = true;
    this.addresses = [];
    this.dataTable.reset();
    this._clearLoadingStatus();
    this.getAllAddresses(0);
  }
}
